/* eslint-disable no-magic-numbers */
import { TextUtil, UIUtil } from '@suntex.core/animation';

import { TextFieldObject } from '../types';

import { BUTTON_RADIUS } from './const';

const LABEL_PADDING_X = 8;
const LABEL_PADDING_Y = 4;
const LABEL_BORDER_RADIUS = 8;
const LABEL_Y_ADJUST = 2;
const BACKGROUND_ALPHA = 0.5;
const BACKGROUND_ALPHA_HOVER = 1.0;

export class TextLabel extends createjs.Container {
  private textFieldTop: TextFieldObject;
  private textFieldBottom: TextFieldObject;
  private background: createjs.Shape | undefined = undefined;

  constructor({
    text,
    angle,
    fontFamily,
  }: {
    text: string;
    angle: number;
    fontFamily: string;
  }) {
    super();

    angle = (angle + 360) % 360;

    let lines: readonly [string, string];

    if (text.search('\n') >= 0) {
      lines = text.split('\n') as [string, string];
    } else {
      if (angle && angle < 180) {
        lines = [text, ''];
      } else {
        lines = ['', text];
      }
    }

    const maxLength = lines.reduce(
      (acc, line) => Math.max(acc, line.length),
      0,
    );

    const size =
      maxLength < 8 ? 28 : maxLength < 16 ? 24 : maxLength < 24 ? 22 : 20;

    const color = '#333333';
    const textAlign = 'center';
    const textBaseline = 'middle';
    this.textFieldTop = TextUtil.createText(lines[0], {
      font: { size, family: fontFamily },
      color,
      textAlign,
      textBaseline,
    });
    this.textFieldBottom = TextUtil.createText(lines[1], {
      font: { size, family: fontFamily },
      color,
      textAlign,
      textBaseline,
    });

    const textPositionRadius = BUTTON_RADIUS + LABEL_PADDING_Y + size / 2;

    this.textFieldTop.y = -textPositionRadius + LABEL_Y_ADJUST;
    this.textFieldBottom.y = textPositionRadius + LABEL_Y_ADJUST;

    this.addChild(this.textFieldTop);
    this.addChild(this.textFieldBottom);
  }

  onHover({ animationMs }: { animationMs: number }) {
    this.initBackground();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.background!.alpha = BACKGROUND_ALPHA_HOVER;
    UIUtil.tween(this, 'alpha', 1.0, animationMs, 'strongOut');
  }

  onLeave({
    animationMs,
    alwaysShow,
  }: {
    animationMs: number;
    alwaysShow: boolean;
  }) {
    this.initBackground();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.background!.alpha = BACKGROUND_ALPHA;

    if (!alwaysShow) {
      UIUtil.tween(this, 'alpha', 0.0, animationMs, 'strongIn');
    }
  }

  private initBackground() {
    if (this.background) return;
    const g = new createjs.Graphics();
    g.beginFill(`rgba(255,255,255,${BACKGROUND_ALPHA_HOVER})`);

    {
      const topBounds =
        this.textFieldTop.getBounds() as createjs.Rectangle | null;
      const width = (topBounds?.width ?? 0) + LABEL_PADDING_X * 2;
      const height = (topBounds?.height ?? 0) + LABEL_PADDING_Y * 2;
      g.drawRoundRect(
        -width / 2,
        this.textFieldTop.y - height / 2 - LABEL_PADDING_Y + LABEL_Y_ADJUST,
        width,
        height,
        LABEL_BORDER_RADIUS,
      );
    }
    {
      const bottomBounds =
        this.textFieldBottom.getBounds() as createjs.Rectangle | null;
      const width = (bottomBounds?.width ?? 0) + LABEL_PADDING_X * 2;
      const height = (bottomBounds?.height ?? 0) + LABEL_PADDING_Y * 2;
      g.drawRoundRect(
        -width / 2,
        this.textFieldBottom.y - height / 2 - LABEL_PADDING_Y + LABEL_Y_ADJUST,
        width,
        height,
        LABEL_BORDER_RADIUS,
      );
    }
    this.background = new createjs.Shape(g);
    this.background.alpha = BACKGROUND_ALPHA;
    this.addChildAt(this.background, 0);
  }
}
